import { useTranslations } from '@aikoa/i18n/react'
import { useMutation } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { View } from 'react-native'
import { useAlert } from '~/components/GlobalAlert'
import { Body } from '~/components/typography/Content'
import { Heading3 } from '~/components/typography/Titles'
import { Button, ButtonLabel } from '~/components/ui/button'
import { Text } from '~/components/ui/text'
import InvoiceForm, {
  initialValues,
  InvoiceFormContext,
  type FormType,
} from '~/components/user/invoicing/InvoiceForm'
import { useSession } from '~/components/user/SessionProvider'
import { invoiceSchema } from '~/data/invoices/types'
import { useInvoices } from '~/data/invoices/useInvoices'
import { queryClient, trpc } from '~/lib/trpc'

type InvoiceErrors = Partial<Record<keyof FormType | 'form', string[]>>

export function InvoicingDetailsView() {
  const { t } = useTranslations('user')
  const { showAlert } = useAlert()
  const { session } = useSession()
  const { data: invoices } = useInvoices()

  const [invoiceValues, setInvoiceValues] = useState<FormType>({
    ...initialValues,
    terms_agreement: true,
  })
  const [invoiceErrors, setInvoiceErrors] = useState<InvoiceErrors>({})

  const hasHydratedInvoice = useRef(false)

  const upsertInvoiceMutation = useMutation(trpc.billing.upsertInvoicingDetails.mutationOptions())

  const latestInvoice = useMemo(() => {
    if (!invoices || invoices.length === 0) {
      return null
    }

    return invoices[0] ?? null
  }, [invoices])

  useEffect(() => {
    if (hasHydratedInvoice.current) {
      return
    }

    if (!latestInvoice) {
      return
    }

    setInvoiceValues({
      organisation_name: latestInvoice.organisation_name ?? '',
      organisation_identifier: latestInvoice.organisation_identifier ?? '',
      e_invoice_address: latestInvoice.e_invoice_address ?? '',
      e_invoice_operator: latestInvoice.e_invoice_operator ?? '',
      reference: latestInvoice.reference ?? '',
      terms_agreement: true,
    })

    hasHydratedInvoice.current = true
  }, [latestInvoice])

  const handleInvoiceValueChange = useCallback(
    <Key extends keyof FormType>(key: Key, value: FormType[Key]) => {
      const fieldSchema = invoiceSchema.shape[key]
      const parseResult = fieldSchema.safeParse(value)
      const errorMessage = parseResult.success ? undefined : parseResult.error.issues[0]?.message

      setInvoiceErrors((previousErrors) => {
        if (!errorMessage) {
          const nextErrors = { ...previousErrors }
          delete nextErrors[key]
          return nextErrors
        }

        return { ...previousErrors, [key]: [errorMessage] }
      })

      setInvoiceValues((previousValues) => ({
        ...previousValues,
        [key]: value,
      }))
    },
    []
  )

  const setInvoiceError = useCallback((key: keyof FormType, message?: string) => {
    setInvoiceErrors((previousErrors) => {
      if (!message) {
        const nextErrors = { ...previousErrors }
        delete nextErrors[key]
        return nextErrors
      }

      return { ...previousErrors, [key]: [message] }
    })
  }, [])

  const handleSetInvoiceErrors = useCallback((errors: InvoiceErrors) => {
    setInvoiceErrors(errors)
  }, [])

  const validateInvoiceForm = useCallback((values: FormType) => {
    const parseResult = invoiceSchema.safeParse(values)

    if (!parseResult.success) {
      const fieldErrors: Partial<Record<keyof FormType, string[]>> = {}

      for (const issue of parseResult.error.issues) {
        const fieldKey = issue.path[0] as keyof FormType
        fieldErrors[fieldKey] = [issue.message]
      }

      setInvoiceErrors(fieldErrors)
      return false
    }

    setInvoiceErrors({})
    return true
  }, [])

  const isInvoiceValid = useMemo(
    () => invoiceSchema.safeParse(invoiceValues).success,
    [invoiceValues]
  )

  const handleSave = useCallback(async () => {
    if (!session?.user.id) {
      return
    }

    const isValid = validateInvoiceForm(invoiceValues)

    if (!isValid) {
      return
    }

    try {
      const response = await upsertInvoiceMutation.mutateAsync({
        organisationName: invoiceValues.organisation_name,
        organisationIdentifier: invoiceValues.organisation_identifier,
        eInvoiceAddress: invoiceValues.e_invoice_address,
        eInvoiceOperator: invoiceValues.e_invoice_operator,
        reference: invoiceValues.reference,
      })

      if (!response.success) {
        showAlert({
          type: 'error',
          title: t(($) => $.invoice.error.title),
          description: t(($) => $.invoice.error.saveFailed),
        })
        return
      }

      await queryClient.invalidateQueries({
        queryKey: ['invoices', session.user.id],
      })
    } catch {
      showAlert({
        type: 'error',
        title: t(($) => $.invoice.error.title),
        description: t(($) => $.invoice.error.saveFailed),
      })
    }
  }, [invoiceValues, session?.user.id, showAlert, t, upsertInvoiceMutation, validateInvoiceForm])

  return (
    <InvoiceFormContext.Provider
      value={{
        values: invoiceValues,
        errors: invoiceErrors,
        setValue: handleInvoiceValueChange,
        setError: setInvoiceError,
        setErrors: handleSetInvoiceErrors,
      }}>
      <View className="gap-6 px-6 pb-6 pt-4">
        <View className="gap-3">
          <Heading3 className="text-center">{t(($) => $.invoice.title)}</Heading3>
          <Body className="text-center text-secondary">{t(($) => $.invoice.description)}</Body>
        </View>

        <InvoiceForm
          inputVariant="box"
          isPending={upsertInvoiceMutation.isPending}
          showAgreement={false}
        />

        {!isInvoiceValid ? (
          <Text className="text-center text-xs text-secondary">
            {t(($) => $.invoice.form.fillAllFields)}
          </Text>
        ) : null}

        <Button
          variant="primary"
          size="lg"
          disabled={!isInvoiceValid || upsertInvoiceMutation.isPending}
          loading={upsertInvoiceMutation.isPending}
          onPress={handleSave}>
          <ButtonLabel loadingLabel={t(($) => $.invoice.form.saving)}>
            {t(($) => $.invoice.form.saveButton)}
          </ButtonLabel>
        </Button>
      </View>
    </InvoiceFormContext.Provider>
  )
}
